import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  FiDownload, FiPrinter, FiSearch, FiDollarSign,
  FiHash, FiExternalLink, FiChevronUp, FiChevronDown,
  FiCreditCard, FiBriefcase
} from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { collection, getDocs, query, orderBy, where } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { getEmployeesCached } from '../../utils/masterDataStore';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import { formatNumber, formatDate } from '../../utils/formatters';
import useCurrency from '../../hooks/useCurrency';
import useReportMemory from '../../hooks/useReportMemory';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';
import { expandMultiPartyTransactions } from '../../utils/multiPartyHelper';
import { addPrintBrand } from '../../utils/printBranding';

// Collection type codes: CR = Cash Receipt, BR = Bank Receipt
const COLLECTION_TYPE_CODES = ['CR', 'BR'];

const COLLECTION_TYPE_LABELS = {
  CR: 'Cash Receipt',
  BR: 'Bank Receipt'
};

// Route map for navigating to receipt pages
const COLLECTION_TYPE_ROUTES = {
  CR: '/receipts/cash-receipt',
  BR: '/receipts/bank-receipt'
};

// Payment type filter options
const PAYMENT_TYPE_OPTIONS = [
  { value: 'All', label: 'All Types' },
  { value: 'CR', label: 'Cash Receipt' },
  { value: 'BR', label: 'Bank Receipt' }
];

// Status options
const STATUS_OPTIONS = [
  'All', 'Draft', 'Posted', 'Cancelled'
];

// Status color map
const getStatusColor = (status) => {
  const s = (status || '').toLowerCase();
  if (s === 'posted' || s === 'approved') return { bg: '#E8F5E9', color: '#2E7D32' };
  if (s === 'draft' || s === 'pending') return { bg: '#FFF8E1', color: '#F57F17' };
  if (s === 'cancelled') return { bg: '#FFEBEE', color: '#C62828' };
  return { bg: colors.neutral[100], color: colors.text.secondary };
};

// Chart colors
const CHART_COLORS = ['#800020', '#A0153E', '#5D0017', '#C4A484', '#8B7355', '#D4AF37'];

const CollectionReport = () => {
  const navigate = useNavigate();
  const { activeCompany, activeFinancialYear, activeBranch } = useAuth();
  // Backward memory: restore filters + generated rows on Back (0 re-reads).
  const [savedMem, saveMemory] = useReportMemory('collection-report');
  const { formatCurrency } = useCurrency();

  // Data state
  const [transactions, setTransactions] = useState(() => savedMem?.transactions ?? []);
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [financialYears, setFinancialYears] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(() => savedMem?.hasLoaded ?? false);

  // Filter state
  const [selectedFYId, setSelectedFYId] = useState(() => savedMem?.selectedFYId ?? '');
  const [dateFrom, setDateFrom] = useState(() => savedMem?.dateFrom ?? '');
  const [dateTo, setDateTo] = useState(() => savedMem?.dateTo ?? '');
  const [customerFilter, setCustomerFilter] = useState(() => savedMem?.customerFilter ?? 'All');
  const [salespersonFilter, setSalespersonFilter] = useState(() => savedMem?.salespersonFilter ?? 'All');
  const [paymentTypeFilter, setPaymentTypeFilter] = useState(() => savedMem?.paymentTypeFilter ?? 'All');
  const [statusFilter, setStatusFilter] = useState(() => savedMem?.statusFilter ?? 'All');
  const [searchQuery, setSearchQuery] = useState(() => savedMem?.searchQuery ?? '');
  const [regions, setRegions] = useState([]);
  const [areas, setAreas] = useState([]);
  const [selectedRegionId, setSelectedRegionId] = useState(() => savedMem?.selectedRegionId ?? 'all');
  const [selectedAreaId, setSelectedAreaId] = useState(() => savedMem?.selectedAreaId ?? 'all');

  // Sort state
  const [sortField, setSortField] = useState(() => savedMem?.sortField ?? 'docDate');
  const [sortDirection, setSortDirection] = useState(() => savedMem?.sortDirection ?? 'desc');

  // Load financial years
  const loadFinancialYears = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const fyRef = collection(db, 'companies', activeCompany.id, 'financialYears');
      const q = query(fyRef, orderBy('startDate', 'desc'));
      const snapshot = await getDocs(q);
      const fys = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setFinancialYears(fys);

      if (activeFinancialYear?.id) {
        setSelectedFYId(activeFinancialYear.id);
      } else if (fys.length > 0) {
        setSelectedFYId(fys[0].id);
      }
    } catch (error) {
      console.error('Error loading financial years:', error);
    }
  }, [activeCompany, activeFinancialYear]);

  // Load dropdown data (customers, employees, regions, areas)
  const loadDropdownData = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      // Load customers
      const custRef = collection(db, 'companies', activeCompany.id, 'customers');
      const custSnapshot = await getDocs(query(custRef, orderBy('companyName')));
      setCustomers(custSnapshot.docs.map(d => ({ id: d.id, ...d.data() })));

      // Load employees (salespersons)
      const empRows = await getEmployeesCached(activeCompany.id);
      setEmployees(empRows.filter(r => r.firstName != null)
        .sort((a, b) => String(a.firstName || '').localeCompare(String(b.firstName || ''))));

      // Load regions
      const regRef = collection(db, 'companies', activeCompany.id, 'regions');
      const regSnapshot = await getDocs(regRef);
      setRegions(regSnapshot.docs.map(d => ({ id: d.id, ...d.data() })));

      // Load areas
      const areaRef = collection(db, 'companies', activeCompany.id, 'areas');
      const areaSnapshot = await getDocs(areaRef);
      setAreas(areaSnapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error loading dropdown data:', error);
    }
  }, [activeCompany]);

  // Initialize
  useEffect(() => {
    loadFinancialYears();
    loadDropdownData();

    // Set default date range to current month — ONLY on a fresh visit.
    // If we restored a saved view (Back navigation), keep the restored dates.
    if (!savedMem) {
      const today = new Date();
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      setDateFrom(firstDay.toISOString().split('T')[0]);
      setDateTo(today.toISOString().split('T')[0]);
    }
  }, [loadFinancialYears, loadDropdownData, savedMem]);

  // Customer map for region/area filtering
  const customerMap = useMemo(() => {
    const map = {};
    customers.forEach(c => { map[c.id] = c; });
    return map;
  }, [customers]);

  // Cascading areas based on selected region
  const filteredAreas = useMemo(() => {
    if (selectedRegionId === 'all') return areas;
    return areas.filter(a => a.regionId === selectedRegionId);
  }, [areas, selectedRegionId]);

  // Load transactions
  const loadTransactions = useCallback(async () => {
    if (!activeCompany?.id) return;

    setLoading(true);
    setHasLoaded(true);

    try {
      const transRef = collection(db, 'companies', activeCompany.id, 'transactions');

      // Build query conditions
      const conditions = [];

      // Filter by financial year
      if (selectedFYId) {
        conditions.push(where('financialYearId', '==', selectedFYId));
      }

      // Filter by payment type or all collection types
      if (paymentTypeFilter !== 'All') {
        conditions.push(where('tranTypeCode', '==', paymentTypeFilter));
      } else {
        conditions.push(where('tranTypeCode', 'in', COLLECTION_TYPE_CODES));
      }

      // Only Posted transactions by default filter (applied in memory for flexibility)
      const q = query(transRef, ...conditions);
      const snapshot = await getDocs(q);

      let allTransactions = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })).filter(t => {
        if (!activeBranch || activeBranch.id === 'all') return true;
        return t.branchId === activeBranch.id;
      });

      // Expand multi-party transactions into per-party rows
      allTransactions = expandMultiPartyTransactions(allTransactions);

      // Apply date filters in memory
      if (dateFrom) {
        allTransactions = allTransactions.filter(t => (t.docDate || '') >= dateFrom);
      }
      if (dateTo) {
        allTransactions = allTransactions.filter(t => (t.docDate || '') <= dateTo);
      }

      // Apply customer filter
      if (customerFilter !== 'All') {
        allTransactions = allTransactions.filter(t => t.partyId === customerFilter);
      }

      // Apply region filter
      if (selectedRegionId !== 'all') {
        allTransactions = allTransactions.filter(t => {
          const cust = customerMap[t.partyId];
          return cust && cust.regionId === selectedRegionId;
        });
      }

      // Apply area filter
      if (selectedAreaId !== 'all') {
        allTransactions = allTransactions.filter(t => {
          const cust = customerMap[t.partyId];
          return cust && cust.areaId === selectedAreaId;
        });
      }

      // Apply salesperson filter
      if (salespersonFilter !== 'All') {
        allTransactions = allTransactions.filter(t => t.employeeId === salespersonFilter);
      }

      // Apply status filter
      if (statusFilter !== 'All') {
        allTransactions = allTransactions.filter(t =>
          (t.status || '').toLowerCase() === statusFilter.toLowerCase()
        );
      }

      // Apply search query on doc number or customer name
      if (searchQuery.trim()) {
        const sq = searchQuery.toLowerCase().trim();
        allTransactions = allTransactions.filter(t =>
          (t.docNumber || '').toLowerCase().includes(sq) ||
          (t.partyName || '').toLowerCase().includes(sq)
        );
      }

      // Sort by docDate descending by default
      allTransactions.sort((a, b) => {
        const dateCompare = (b.docDate || '').localeCompare(a.docDate || '');
        if (dateCompare !== 0) return dateCompare;
        return (b.docNumber || '').localeCompare(a.docNumber || '');
      });

      setTransactions(allTransactions);
      // Snapshot for backward memory: restore filters + rows on Back (0 re-reads).
      saveMemory({
        transactions: allTransactions,
        hasLoaded: true,
        selectedFYId, dateFrom, dateTo, customerFilter, salespersonFilter,
        paymentTypeFilter, statusFilter, searchQuery,
        selectedRegionId, selectedAreaId, sortField, sortDirection
      });
    } catch (error) {
      console.error('Error loading collection transactions:', error);
    } finally {
      setLoading(false);
    }
  }, [activeCompany, selectedFYId, paymentTypeFilter, dateFrom, dateTo,
      customerFilter, salespersonFilter, statusFilter, searchQuery, activeBranch,
      selectedRegionId, selectedAreaId, customerMap, saveMemory, sortField, sortDirection]);

  // Sort handler
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Sorted transactions
  const sortedTransactions = useMemo(() => {
    const sorted = [...transactions];
    sorted.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      // Handle numeric fields
      if (['totalAmount', 'netAmount'].includes(sortField)) {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      } else {
        aVal = (aVal || '').toString().toLowerCase();
        bVal = (bVal || '').toString().toLowerCase();
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [transactions, sortField, sortDirection]);

  // Summary calculations
  const summary = useMemo(() => {
    const posted = transactions.filter(t => (t.status || '').toLowerCase() === 'posted');
    const cashCollections = transactions.filter(t => t.tranTypeCode === 'CR');
    const bankCollections = transactions.filter(t => t.tranTypeCode === 'BR');

    return {
      totalCollected: transactions.reduce((sum, t) => sum + (Number(t.netAmount) || Number(t.totalAmount) || 0), 0),
      cashTotal: cashCollections.reduce((sum, t) => sum + (Number(t.netAmount) || Number(t.totalAmount) || 0), 0),
      bankTotal: bankCollections.reduce((sum, t) => sum + (Number(t.netAmount) || Number(t.totalAmount) || 0), 0),
      collectionCount: transactions.length,
      cashCount: cashCollections.length,
      bankCount: bankCollections.length,
      postedCount: posted.length
    };
  }, [transactions]);

  // Chart data: Collection trend over time (Area chart)
  const trendChartData = useMemo(() => {
    if (transactions.length === 0) return [];

    // Determine if we should group by day or month
    const dateSpread = new Set(transactions.map(t => t.docDate)).size;
    const useMonthly = dateSpread > 60;

    const grouped = {};
    transactions.forEach(t => {
      const date = t.docDate || '';
      let key;
      if (useMonthly && date.length >= 7) {
        key = date.substring(0, 7); // YYYY-MM
      } else {
        key = date;
      }
      if (!grouped[key]) {
        grouped[key] = { period: key, cashAmount: 0, bankAmount: 0, totalAmount: 0 };
      }
      const amt = Number(t.netAmount) || Number(t.totalAmount) || 0;
      grouped[key].totalAmount += amt;
      if (t.tranTypeCode === 'CR') {
        grouped[key].cashAmount += amt;
      } else {
        grouped[key].bankAmount += amt;
      }
    });

    return Object.values(grouped).sort((a, b) => a.period.localeCompare(b.period)).map(item => ({
      ...item,
      label: useMonthly
        ? formatMonthLabel(item.period)
        : formatShortDate(item.period)
    }));
  }, [transactions]);

  // Chart data: Cash vs Bank split (Pie chart)
  const paymentSplitData = useMemo(() => {
    if (transactions.length === 0) return [];

    const data = [];
    if (summary.cashTotal > 0) {
      data.push({ name: 'Cash Receipts', value: summary.cashTotal, count: summary.cashCount });
    }
    if (summary.bankTotal > 0) {
      data.push({ name: 'Bank Receipts', value: summary.bankTotal, count: summary.bankCount });
    }
    return data;
  }, [transactions, summary]);

  // Chart data: Top collectors by salesperson (Bar chart)
  const salespersonChartData = useMemo(() => {
    if (transactions.length === 0) return [];

    const grouped = {};
    transactions.forEach(t => {
      const spName = t.employeeName || 'Unassigned';
      const spId = t.employeeId || 'unassigned';
      if (!grouped[spId]) {
        grouped[spId] = { name: spName, amount: 0, count: 0 };
      }
      grouped[spId].amount += Number(t.netAmount) || Number(t.totalAmount) || 0;
      grouped[spId].count += 1;
    });

    return Object.values(grouped)
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 10);
  }, [transactions]);

  // Navigate to document
  const handleDocClick = (tranTypeCode, transactionId) => {
    const route = COLLECTION_TYPE_ROUTES[tranTypeCode];
    if (route) {
      navigate(route, { state: { viewTransactionId: transactionId } });
    }
  };

  // CSV Export
  const handleExport = () => {
    if (sortedTransactions.length === 0) return;

    const selectedFY = financialYears.find(f => f.id === selectedFYId);

    let csv = `Collection Report\n`;
    csv += `Company: ${activeCompany?.companyName || ''}\n`;
    csv += `Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || ''}\n`;
    csv += `Period: ${dateFrom ? formatDate(dateFrom) : 'Start'} to ${dateTo ? formatDate(dateTo) : 'End'}\n`;
    csv += `Total Collected: ${formatCurrency(summary.totalCollected)}\n`;
    csv += `Cash Collections: ${formatCurrency(summary.cashTotal)} | Bank Collections: ${formatCurrency(summary.bankTotal)}\n\n`;
    csv += `#,Date,Doc Number,Type,Customer,Salesperson,Amount,Payment Mode,Status\n`;

    sortedTransactions.forEach((t, idx) => {
      const amt = Number(t.netAmount) || Number(t.totalAmount) || 0;
      csv += `${idx + 1},`;
      csv += `${formatDate(t.docDate)},`;
      csv += `${t.docNumber || ''},`;
      csv += `${COLLECTION_TYPE_LABELS[t.tranTypeCode] || t.tranTypeCode || ''},`;
      csv += `"${(t.partyName || '').replace(/"/g, '""')}",`;
      csv += `"${(t.employeeName || '').replace(/"/g, '""')}",`;
      csv += `${amt},`;
      csv += `${t.tranTypeCode === 'CR' ? 'Cash' : 'Bank'},`;
      csv += `${t.status || ''}\n`;
    });

    csv += `\n,,,,Total,,${summary.totalCollected},,\n`;
    csv += `\nTotal Collections: ${summary.collectionCount}\n`;
    csv += `Cash Receipts: ${summary.cashCount} (${formatCurrency(summary.cashTotal)})\n`;
    csv += `Bank Receipts: ${summary.bankCount} (${formatCurrency(summary.bankTotal)})\n`;

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Collection_Report_${dateFrom || 'all'}_to_${dateTo || 'all'}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Print
  const handlePrint = () => {
    if (sortedTransactions.length === 0) return;

    const selectedFY = financialYears.find(f => f.id === selectedFYId);

    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Collection Report</title>
      <style>
        body{font-family:'Segoe UI',sans-serif;margin:30px;color:#1A1A1A;font-size:12px}
        h1{color:#800020;font-size:20px;margin-bottom:5px}
        .meta{color:#666;font-size:12px;margin-bottom:15px}
        .summary{display:flex;gap:24px;margin-bottom:20px;padding:12px;background:#f9f9f9;border-radius:8px}
        .summary-item{text-align:center}
        .summary-label{font-size:10px;color:#666;margin-bottom:3px}
        .summary-value{font-size:14px;font-weight:600}
        table{width:100%;border-collapse:collapse;margin-bottom:15px}
        th{background:#800020;color:white;padding:6px 8px;text-align:left;font-size:10px;text-transform:uppercase}
        td{padding:6px 8px;border-bottom:1px solid #ddd;font-size:11px}
        .amount{text-align:right;font-family:'Consolas',monospace}
        .totals-row{background:#f5f5f5;font-weight:600}
        .status-badge{padding:2px 6px;border-radius:3px;font-size:10px;font-weight:500}
        .type-badge{padding:2px 6px;border-radius:3px;font-size:10px;font-weight:500}
        .cash{background:#E8F5E9;color:#2E7D32}
        .bank{background:#E3F2FD;color:#1565C0}
        @media print{body{margin:15px}h1{font-size:16px}}
      </style></head><body>
      <h1>Collection Report</h1>
      <div class="meta">
        ${activeCompany?.companyName || 'Company'} |
        Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || ''} |
        Period: ${dateFrom ? formatDate(dateFrom) : 'Start'} to ${dateTo ? formatDate(dateTo) : 'End'} |
        Collections: ${summary.collectionCount}
      </div>
      <div class="summary">
        <div class="summary-item">
          <div class="summary-label">Total Collected</div>
          <div class="summary-value">${formatCurrency(summary.totalCollected)}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">Cash Collections</div>
          <div class="summary-value">${formatCurrency(summary.cashTotal)}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">Bank Collections</div>
          <div class="summary-value">${formatCurrency(summary.bankTotal)}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">Collection Count</div>
          <div class="summary-value">${formatNumber(summary.collectionCount)}</div>
        </div>
      </div>
      <table>
        <thead><tr>
          <th>#</th>
          <th>Date</th>
          <th>Doc Number</th>
          <th>Type</th>
          <th>Customer</th>
          <th>Salesperson</th>
          <th class="amount">Amount</th>
          <th>Payment Mode</th>
          <th>Status</th>
        </tr></thead>
        <tbody>
          ${sortedTransactions.map((t, idx) => {
            const amt = Number(t.netAmount) || Number(t.totalAmount) || 0;
            const typeClass = t.tranTypeCode === 'CR' ? 'cash' : 'bank';
            return `
            <tr>
              <td>${idx + 1}</td>
              <td>${formatDate(t.docDate)}</td>
              <td>${t.docNumber || '-'}</td>
              <td><span class="type-badge ${typeClass}">${COLLECTION_TYPE_LABELS[t.tranTypeCode] || t.tranTypeCode || '-'}</span></td>
              <td>${t.partyName || '-'}</td>
              <td>${t.employeeName || '-'}</td>
              <td class="amount">${formatCurrency(amt)}</td>
              <td>${t.tranTypeCode === 'CR' ? 'Cash' : 'Bank'}</td>
              <td>${t.status || '-'}</td>
            </tr>
          `}).join('')}
          <tr class="totals-row">
            <td colspan="6" style="text-align:right">Totals (${sortedTransactions.length} collections)</td>
            <td class="amount">${formatCurrency(summary.totalCollected)}</td>
            <td></td>
            <td></td>
          </tr>
        </tbody>
      </table>
    </body></html>`);
    addPrintBrand(w);
    w.document.close();
    w.print();
  };

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <TooltipBox>
          <TooltipLabel>{label}</TooltipLabel>
          {payload.map((entry, index) => (
            <TooltipRow key={index}>
              <TooltipDot style={{ background: entry.color }} />
              <span>{entry.name}: </span>
              <strong>
                {entry.name === 'Count'
                  ? formatNumber(entry.value)
                  : formatCurrency(entry.value)}
              </strong>
            </TooltipRow>
          ))}
        </TooltipBox>
      );
    }
    return null;
  };

  // Custom tooltip for pie chart
  const PieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <TooltipBox>
          <TooltipLabel>{data.name}</TooltipLabel>
          <TooltipRow>
            <TooltipDot style={{ background: data.payload.fill }} />
            <span>Amount: </span>
            <strong>{formatCurrency(data.value)}</strong>
          </TooltipRow>
          <TooltipRow>
            <TooltipDot style={{ background: 'transparent' }} />
            <span>Count: </span>
            <strong>{formatNumber(data.payload.count)}</strong>
          </TooltipRow>
        </TooltipBox>
      );
    }
    return null;
  };

  // Pie chart custom label
  const renderPieLabel = ({ name, percent }) => {
    return `${name} (${(percent * 100).toFixed(1)}%)`;
  };

  return (
    <PageLayout pageTitle="Collection Report" breadcrumb="Reports / Sale Reports / Collection Report">
      {/* Filters */}
      <FiltersCard>
        <FiltersGrid>
          <FilterGroup>
            <Label>Financial Year</Label>
            <Select value={selectedFYId} onChange={(e) => setSelectedFYId(e.target.value)}>
              <option value="">All Financial Years</option>
              {financialYears.map(fy => (
                <option key={fy.id} value={fy.id}>
                  {fy.yearName || fy.yearCode}
                  {fy.isActive ? ' (Active)' : ''}
                </option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Date From</Label>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </FilterGroup>

          <FilterGroup>
            <Label>Date To</Label>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </FilterGroup>

          <FilterGroup>
            <Label>Customer</Label>
            <Select value={customerFilter} onChange={(e) => setCustomerFilter(e.target.value)}>
              <option value="All">All Customers</option>
              {customers.map(c => (
                <option key={c.id} value={c.id}>{c.companyName}</option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Region</Label>
            <Select value={selectedRegionId} onChange={e => { setSelectedRegionId(e.target.value); setSelectedAreaId('all'); }}>
              <option value="all">All Regions</option>
              {regions.map(r => (
                <option key={r.id} value={r.id}>{r.regionName}</option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Area</Label>
            <Select value={selectedAreaId} onChange={e => setSelectedAreaId(e.target.value)}>
              <option value="all">All Areas</option>
              {filteredAreas.map(a => (
                <option key={a.id} value={a.id}>{a.areaName}</option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Salesperson</Label>
            <Select value={salespersonFilter} onChange={(e) => setSalespersonFilter(e.target.value)}>
              <option value="All">All Salespersons</option>
              {employees.map(e => (
                <option key={e.id} value={e.id}>{e.fullName || e.firstName}</option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Payment Type</Label>
            <Select value={paymentTypeFilter} onChange={(e) => setPaymentTypeFilter(e.target.value)}>
              {PAYMENT_TYPE_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Status</Label>
            <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              {STATUS_OPTIONS.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Search</Label>
            <Input
              type="text"
              placeholder="Doc # or customer..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </FilterGroup>
        </FiltersGrid>

        <ButtonGroup>
          <Button onClick={handleExport} disabled={sortedTransactions.length === 0}>
            <FiDownload /> Export CSV
          </Button>
          <Button onClick={handlePrint} disabled={sortedTransactions.length === 0}>
            <FiPrinter /> Print
          </Button>
          <Button primary onClick={loadTransactions} disabled={loading}>
            <FiSearch /> {loading ? 'Loading...' : 'Generate Report'}
          </Button>
        </ButtonGroup>
      </FiltersCard>

      {/* Summary Cards */}
      {hasLoaded && (
        <SummaryCards>
          <SummaryCardStyled>
            <SummaryIcon color={colors.primary.main}>
              <FiDollarSign />
            </SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Total Collected</SummaryLabel>
              <SummaryValue>{formatCurrency(summary.totalCollected)}</SummaryValue>
            </SummaryInfo>
          </SummaryCardStyled>

          <SummaryCardStyled>
            <SummaryIcon color={colors.success.main}>
              <FiCreditCard />
            </SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Cash Collections (CR)</SummaryLabel>
              <SummaryValue>{formatCurrency(summary.cashTotal)}</SummaryValue>
              <SummarySubtext>{formatNumber(summary.cashCount)} receipts</SummarySubtext>
            </SummaryInfo>
          </SummaryCardStyled>

          <SummaryCardStyled>
            <SummaryIcon color={colors.info.main}>
              <FiBriefcase />
            </SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Bank Collections (BR)</SummaryLabel>
              <SummaryValue>{formatCurrency(summary.bankTotal)}</SummaryValue>
              <SummarySubtext>{formatNumber(summary.bankCount)} receipts</SummarySubtext>
            </SummaryInfo>
          </SummaryCardStyled>

          <SummaryCardStyled>
            <SummaryIcon color={colors.chart.accent3}>
              <FiHash />
            </SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Collection Count</SummaryLabel>
              <SummaryValue>{formatNumber(summary.collectionCount)}</SummaryValue>
            </SummaryInfo>
          </SummaryCardStyled>
        </SummaryCards>
      )}

      {/* Data Table */}
      <ContentCard>
        <TableContainer>
          {loading ? (
            <EmptyState>
              <EmptyText>Loading collection transactions...</EmptyText>
            </EmptyState>
          ) : !hasLoaded ? (
            <EmptyState>
              <EmptyIcon><FiDollarSign /></EmptyIcon>
              <EmptyText>Select filters and click "Generate Report" to view the collection report</EmptyText>
            </EmptyState>
          ) : sortedTransactions.length === 0 ? (
            <EmptyState>
              <EmptyIcon><FiDollarSign /></EmptyIcon>
              <EmptyText>No collection transactions found for the selected criteria</EmptyText>
            </EmptyState>
          ) : (
            <Table>
              <Thead>
                <Tr>
                  <Th>#</Th>
                  <ThSortable
                    onClick={() => handleSort('docDate')}
                    $active={sortField === 'docDate'}
                  >
                    Date
                    <SortIcon>
                      {sortField === 'docDate'
                        ? (sortDirection === 'asc' ? <FiChevronUp size={12} /> : <FiChevronDown size={12} />)
                        : <FiChevronDown size={12} style={{ opacity: 0.3 }} />
                      }
                    </SortIcon>
                  </ThSortable>
                  <ThSortable
                    onClick={() => handleSort('docNumber')}
                    $active={sortField === 'docNumber'}
                  >
                    Doc #
                    <SortIcon>
                      {sortField === 'docNumber'
                        ? (sortDirection === 'asc' ? <FiChevronUp size={12} /> : <FiChevronDown size={12} />)
                        : <FiChevronDown size={12} style={{ opacity: 0.3 }} />
                      }
                    </SortIcon>
                  </ThSortable>
                  <ThSortable
                    onClick={() => handleSort('tranTypeCode')}
                    $active={sortField === 'tranTypeCode'}
                  >
                    Type
                    <SortIcon>
                      {sortField === 'tranTypeCode'
                        ? (sortDirection === 'asc' ? <FiChevronUp size={12} /> : <FiChevronDown size={12} />)
                        : <FiChevronDown size={12} style={{ opacity: 0.3 }} />
                      }
                    </SortIcon>
                  </ThSortable>
                  <ThSortable
                    onClick={() => handleSort('partyName')}
                    $active={sortField === 'partyName'}
                  >
                    Customer
                    <SortIcon>
                      {sortField === 'partyName'
                        ? (sortDirection === 'asc' ? <FiChevronUp size={12} /> : <FiChevronDown size={12} />)
                        : <FiChevronDown size={12} style={{ opacity: 0.3 }} />
                      }
                    </SortIcon>
                  </ThSortable>
                  <ThSortable
                    onClick={() => handleSort('employeeName')}
                    $active={sortField === 'employeeName'}
                  >
                    Salesperson
                    <SortIcon>
                      {sortField === 'employeeName'
                        ? (sortDirection === 'asc' ? <FiChevronUp size={12} /> : <FiChevronDown size={12} />)
                        : <FiChevronDown size={12} style={{ opacity: 0.3 }} />
                      }
                    </SortIcon>
                  </ThSortable>
                  <ThSortable
                    align="right"
                    onClick={() => handleSort('netAmount')}
                    $active={sortField === 'netAmount'}
                  >
                    Amount
                    <SortIcon>
                      {sortField === 'netAmount'
                        ? (sortDirection === 'asc' ? <FiChevronUp size={12} /> : <FiChevronDown size={12} />)
                        : <FiChevronDown size={12} style={{ opacity: 0.3 }} />
                      }
                    </SortIcon>
                  </ThSortable>
                  <Th>Payment Mode</Th>
                  <ThSortable
                    onClick={() => handleSort('status')}
                    $active={sortField === 'status'}
                  >
                    Status
                    <SortIcon>
                      {sortField === 'status'
                        ? (sortDirection === 'asc' ? <FiChevronUp size={12} /> : <FiChevronDown size={12} />)
                        : <FiChevronDown size={12} style={{ opacity: 0.3 }} />
                      }
                    </SortIcon>
                  </ThSortable>
                </Tr>
              </Thead>
              <Tbody>
                {sortedTransactions.map((t, idx) => {
                  const amt = Number(t.netAmount) || Number(t.totalAmount) || 0;
                  return (
                    <Tr key={t.id}>
                      <Td>{idx + 1}</Td>
                      <Td>{formatDate(t.docDate)}</Td>
                      <Td>
                        <DocNumber
                          onClick={() => handleDocClick(t.tranTypeCode, t.id)}
                          title="Click to open receipt"
                        >
                          {t.docNumber || '-'} <FiExternalLink size={11} />
                        </DocNumber>
                      </Td>
                      <Td>
                        <TypeBadge $type={t.tranTypeCode}>
                          {COLLECTION_TYPE_LABELS[t.tranTypeCode] || t.tranTypeCode || '-'}
                        </TypeBadge>
                      </Td>
                      <Td>{t.partyName || '-'}</Td>
                      <Td>{t.employeeName || '-'}</Td>
                      <AmountCell align="right" style={{ fontWeight: 600 }}>
                        {formatCurrency(amt)}
                      </AmountCell>
                      <Td>
                        <PaymentModeBadge $mode={t.tranTypeCode}>
                          {t.tranTypeCode === 'CR' ? 'Cash' : 'Bank'}
                        </PaymentModeBadge>
                      </Td>
                      <Td>
                        <StatusBadge $statusColor={getStatusColor(t.status)}>
                          {t.status || '-'}
                        </StatusBadge>
                      </Td>
                    </Tr>
                  );
                })}

                {/* Totals Row */}
                <TotalsRow>
                  <Td colSpan={6} style={{ textAlign: 'right', fontWeight: 700 }}>
                    Totals ({sortedTransactions.length} collections)
                  </Td>
                  <AmountCell align="right" style={{ fontWeight: 700 }}>
                    {formatCurrency(summary.totalCollected)}
                  </AmountCell>
                  <Td></Td>
                  <Td></Td>
                </TotalsRow>
              </Tbody>
            </Table>
          )}
        </TableContainer>
      </ContentCard>

      {/* Charts */}
      {hasLoaded && transactions.length > 0 && (
        <>
          {/* Row 1: Area chart + Pie chart */}
          <ChartsRow>
            <ChartCard style={{ flex: 2 }}>
              <ChartTitle>Collection Trend Over Time</ChartTitle>
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={trendChartData}>
                  <defs>
                    <linearGradient id="gradCash" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={CHART_COLORS[0]} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={CHART_COLORS[0]} stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gradBank" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={CHART_COLORS[1]} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={CHART_COLORS[1]} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.neutral[200]} />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11, fill: colors.text.secondary }}
                    axisLine={{ stroke: colors.neutral[300] }}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: colors.text.secondary }}
                    axisLine={{ stroke: colors.neutral[300] }}
                    tickFormatter={(v) => formatCurrency(v, { compact: true, symbol: '' })}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="cashAmount"
                    name="Cash"
                    stroke={CHART_COLORS[0]}
                    fill="url(#gradCash)"
                    strokeWidth={2}
                  />
                  <Area
                    type="monotone"
                    dataKey="bankAmount"
                    name="Bank"
                    stroke={CHART_COLORS[1]}
                    fill="url(#gradBank)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard style={{ flex: 1 }}>
              <ChartTitle>Cash vs Bank Split</ChartTitle>
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={paymentSplitData}
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    innerRadius={45}
                    dataKey="value"
                    nameKey="name"
                    label={renderPieLabel}
                    labelLine={{ stroke: colors.neutral[400] }}
                  >
                    {paymentSplitData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={CHART_COLORS[index % CHART_COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<PieTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <PieLegend>
                {paymentSplitData.map((item, index) => (
                  <PieLegendItem key={item.name}>
                    <PieLegendDot style={{ background: CHART_COLORS[index % CHART_COLORS.length] }} />
                    <PieLegendLabel>
                      <span>{item.name}</span>
                      <PieLegendValue>{formatCurrency(item.value)}</PieLegendValue>
                    </PieLegendLabel>
                  </PieLegendItem>
                ))}
              </PieLegend>
            </ChartCard>
          </ChartsRow>

          {/* Row 2: Top collectors by salesperson */}
          {salespersonChartData.length > 0 && (
            <ChartsRowFull>
              <ChartCard>
                <ChartTitle>Top Collectors by Salesperson</ChartTitle>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={salespersonChartData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke={colors.neutral[200]} />
                    <XAxis
                      type="number"
                      tick={{ fontSize: 11, fill: colors.text.secondary }}
                      axisLine={{ stroke: colors.neutral[300] }}
                      tickFormatter={(v) => formatCurrency(v, { compact: true, symbol: '' })}
                    />
                    <YAxis
                      type="category"
                      dataKey="name"
                      width={130}
                      tick={{ fontSize: 12, fill: colors.text.primary }}
                      axisLine={{ stroke: colors.neutral[300] }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar
                      dataKey="amount"
                      name="Collection Amount"
                      fill={CHART_COLORS[0]}
                      radius={[0, 4, 4, 0]}
                      barSize={22}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </ChartsRowFull>
          )}
        </>
      )}
    </PageLayout>
  );
};

// Helper: format month label from YYYY-MM
const formatMonthLabel = (period) => {
  if (!period || period.length < 7) return period;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const parts = period.split('-');
  const monthIdx = parseInt(parts[1], 10) - 1;
  return `${months[monthIdx] || parts[1]} ${parts[0].slice(2)}`;
};

// Helper: format short date from YYYY-MM-DD
const formatShortDate = (dateStr) => {
  if (!dateStr || dateStr.length < 10) return dateStr;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const parts = dateStr.split('-');
  const monthIdx = parseInt(parts[1], 10) - 1;
  return `${parseInt(parts[2], 10)} ${months[monthIdx] || parts[1]}`;
};

// ============================================================
// Styled Components
// ============================================================

const FiltersCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  margin-bottom: 20px;
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  margin-bottom: 16px;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  color: ${colors.text.primary};
  background: white;
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  color: ${colors.text.primary};
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }

  &::placeholder {
    color: ${colors.text.tertiary};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;

  ${props => props.primary ? `
    background: ${colors.primary.main};
    color: white;
    &:hover {
      background: ${colors.primary.dark};
    }
  ` : `
    background: ${colors.neutral[100]};
    color: ${colors.text.primary};
    &:hover {
      background: ${colors.neutral[200]};
    }
  `}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SummaryCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
`;

const SummaryCardStyled = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SummaryIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${colors.borderRadius.md};
  background: ${props => props.color || colors.primary.main}15;
  color: ${props => props.color || colors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
`;

const SummaryInfo = styled.div`
  min-width: 0;
`;

const SummaryLabel = styled.div`
  font-size: 12px;
  color: ${colors.text.secondary};
  margin-bottom: 4px;
`;

const SummaryValue = styled.div`
  font-size: 22px;
  font-weight: 600;
  color: ${colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SummarySubtext = styled.div`
  font-size: 12px;
  color: ${colors.text.tertiary};
  margin-top: 2px;
`;

const ChartsRow = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 1024px) {
    flex-direction: column;
  }
`;

const ChartsRowFull = styled.div`
  margin-bottom: 20px;
`;

const ChartCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  min-width: 0;
`;

const ChartTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin: 0 0 16px 0;
`;

const PieLegend = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 16px;
  margin-top: 8px;
`;

const PieLegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const PieLegendDot = styled.span`
  width: 12px;
  height: 12px;
  border-radius: 3px;
  flex-shrink: 0;
`;

const PieLegendLabel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 1;
  font-size: 13px;
  color: ${colors.text.primary};
`;

const PieLegendValue = styled.span`
  font-weight: 600;
  font-size: 13px;
  color: ${colors.text.primary};
`;

const TooltipBox = styled.div`
  background: white;
  border: 1px solid ${colors.neutral[200]};
  border-radius: ${colors.borderRadius.md};
  padding: 12px;
  box-shadow: ${colors.shadows.md};
`;

const TooltipLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin-bottom: 8px;
`;

const TooltipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: ${colors.text.secondary};
  margin-bottom: 4px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const TooltipDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
`;

const ContentCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  overflow: hidden;
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: ${colors.neutral[50]};
`;

const Th = styled.th`
  padding: 12px 16px;
  text-align: ${props => props.align || 'left'};
  font-size: 12px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid ${colors.neutral[200]};
  white-space: nowrap;
`;

const ThSortable = styled(Th)`
  cursor: pointer;
  user-select: none;
  transition: color 0.2s;

  &:hover {
    color: ${colors.primary.main};
  }

  ${props => props.$active && `
    color: ${colors.primary.main};
  `}
`;

const SortIcon = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: 4px;
  vertical-align: middle;
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid ${colors.neutral[100]};
  transition: background 0.2s;

  &:hover {
    background: ${colors.neutral[50]};
  }
`;

const TotalsRow = styled.tr`
  background: ${colors.neutral[100]};
  border-top: 2px solid ${colors.neutral[300]};

  &:hover {
    background: ${colors.neutral[100]};
  }
`;

const Td = styled.td`
  padding: 12px 16px;
  font-size: 14px;
  color: ${colors.text.primary};
  text-align: ${props => props.align || 'left'};
`;

const AmountCell = styled(Td)`
  font-family: 'SF Mono', 'Consolas', monospace;
  font-size: 13px;
`;

const DocNumber = styled.span`
  font-weight: 500;
  color: ${colors.primary.main};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 6px;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background: ${colors.primary.main}15;
    text-decoration: underline;
  }

  svg {
    opacity: 0.6;
  }

  &:hover svg {
    opacity: 1;
  }
`;

const TypeBadge = styled.span`
  display: inline-block;
  padding: 4px 8px;
  border-radius: ${colors.borderRadius.sm};
  font-size: 11px;
  font-weight: 500;
  background: ${props => {
    const t = props.$type;
    if (t === 'CR') return '#E8F5E9';
    if (t === 'BR') return '#E3F2FD';
    return colors.neutral[200];
  }};
  color: ${props => {
    const t = props.$type;
    if (t === 'CR') return '#2E7D32';
    if (t === 'BR') return '#1565C0';
    return colors.text.secondary;
  }};
`;

const PaymentModeBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  background: ${props => {
    const m = props.$mode;
    if (m === 'CR') return '#E8F5E9';
    if (m === 'BR') return '#E3F2FD';
    return colors.neutral[100];
  }};
  color: ${props => {
    const m = props.$mode;
    if (m === 'CR') return '#2E7D32';
    if (m === 'BR') return '#1565C0';
    return colors.text.secondary;
  }};
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  background: ${props => props.$statusColor?.bg || colors.neutral[100]};
  color: ${props => props.$statusColor?.color || colors.text.secondary};
  text-transform: capitalize;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${colors.text.secondary};
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  color: ${colors.neutral[300]};
  margin-bottom: 16px;
`;

const EmptyText = styled.p`
  font-size: 16px;
  margin-bottom: 8px;
`;

export default CollectionReport;
